import React, { memo, useCallback, useMemo } from 'react'
import { Route, useHistory } from 'react-router-dom'
import compact from 'lodash/compact'
import Typography from 'transfix-ui/core/Typography'
import makeStyles from 'transfix-ui/core/styles/makeStyles'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { MarketplaceLoadDetailsModalContainer } from 'apps/fleet-planner/pages/marketplace/MarketplaceLoadDetailsContainer'
import { TEST_IDS } from '../../constants'
import ShipmentOffersCard from './ShipmentOffersCard'
import ShipmentOffersCardMobile from './ShipmentOffersCardMobile'
import { ICurrentOfferedShipmentFragment } from './graphql/CurrentOfferedShipmentFragment'
import { useCurrentOffersQuery } from './graphql/CurrentOffers'

export interface IShipmentOffersSectionProps {}

const useShipmentOffersSectionStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  link: {
    color: brandColors.skyBlue2,
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': {
      color: brandColors.skyBlue2,
      textDecoration: 'underline',
    },
  },
}))

const ShipmentOffersSection = memo<IShipmentOffersSectionProps>(() => {
  const styles = useShipmentOffersSectionStyles()
  const history = useHistory()
  const { trackEvent } = useFMSAnalytics()
  const { isMobile } = useBreakpointContext()

  const { data, loading, refetch } = useCurrentOffersQuery({ fetchPolicy: 'cache-and-network' })
  const { offeredShipmentConnection } = data || {}
  const { nodes, totalCount } = offeredShipmentConnection || {}
  const offeredShipments = compact<ICurrentOfferedShipmentFragment>(nodes)

  const offerText = useMemo(() => {
    return totalCount ? `View All Offers (${totalCount})` : 'View All Offers'
  }, [totalCount])

  const viewAllClickHandler = useCallback(() => {
    trackEvent('Homepage', 'HOME_OFFERS_CLICKED_VIEW_ALL', { totalCount })
    history.push('/offers')
  }, [history, totalCount, trackEvent])

  const declineSuccessHandler = useCallback(() => {
    refetch()
  }, [refetch])

  const closeModalHandler = useCallback(() => {
    history.replace(`/home${history.location.search}`)
  }, [history])

  return (
    <div data-test={TEST_IDS.shipmentOffersSection}>
      {!isMobile && (
        <div className={styles.header}>
          <Typography variant='h4' color='secondary'>
            Loads Offered to You
          </Typography>
          <div
            className={styles.link}
            onClick={viewAllClickHandler}
            role='button'
            tabIndex={0}
            aria-hidden='true'>
            <Typography variant='body2' component='div'>
              {offerText}
            </Typography>
          </div>
        </div>
      )}
      {isMobile ? (
        <ShipmentOffersCardMobile
          offeredShipmentsCount={offeredShipments.length}
          loading={loading}
        />
      ) : (
        <ShipmentOffersCard
          offeredShipments={offeredShipments}
          loading={loading}
          onDeclineSuccess={declineSuccessHandler}
        />
      )}
      <Route exact path='/home/:shipmentId/book'>
        <MarketplaceLoadDetailsModalContainer
          route='home'
          onModalClose={closeModalHandler}
          onShipmentDecline={declineSuccessHandler}
        />
      </Route>
    </div>
  )
})

export default ShipmentOffersSection
